class PDPProductOverviewSellingPlan extends HTMLElement {
  constructor() {
    super();
  }
  //   Called when the element is connected to the DOM.
  connectedCallback() {
    this.section = this;

    this.sectionClickHandlerBound = this.sectionClickHandler.bind(this);
    this.section.addEventListener("click", this.sectionClickHandlerBound);
    this.dropdown = this.section.querySelector(".select-wrapper");
    this.oneTimePriceElement = this.section.querySelector(
      `.selling-plan-item[data-selling-plan="one-time"] .selling-plan-item-price-current`
    );
    this.oneTimeComparePriceElement = this.section.querySelector(
      `.selling-plan-item[data-selling-plan="one-time"] .selling-plan-item-price-compare`
    );
    this.subscriptionPriceElement = this.section.querySelector(
      `.selling-plan-item[data-selling-plan="subscription"] .selling-plan-item-price-current`
    );
    this.subscriptionComparePriceElement = this.section.querySelector(
      `.selling-plan-item[data-selling-plan="subscription"] .selling-plan-item-price-compare`
    );
    this.priceAdjustmentType = this.dataset.sellingPlanAdjustmentType;
    this.priceAdjustmentValue = Number(
      this.dataset.sellingPlanAdjustmentValue || 0
    );
    this.variantPriceUpdateHandler = this.handleVariantPriceUpdate.bind(this);
    document.addEventListener(
      "pdp:variant-price-update",
      this.variantPriceUpdateHandler
    );
  }

  //   Called when the element is disconnected from the DOM.
  disconnectedCallback() {
    this.section.removeEventListener("click", this.sectionClickHandlerBound);
    document.removeEventListener(
      "pdp:variant-price-update",
      this.variantPriceUpdateHandler
    );
  }

  /**
   * Event handler for click events on the selling plan section.
   * Determines the clicked selling plan and performs necessary actions.
   * @param {Event} event - The click event
   */
  sectionClickHandler(event) {
    const sellingPlanItem = event.target.closest(".selling-plan-item");
    if (!sellingPlanItem) return;
    const { sellingPlan } = sellingPlanItem.dataset;

    this.removeActiveElement();
    sellingPlanItem.dataset.sellingPlanActive = true;

    if (sellingPlan === "subscription") this.showSellingPlansDropdown(true);
    else this.showSellingPlansDropdown(false);
  }

  /**
   * Shows or hides the selling plans dropdown based on the provided flag.
   * @param {boolean} flag - Flag indicating whether to show (true) or hide (false) the dropdown
   */
  showSellingPlansDropdown(flag) {
    this.dropdown.dataset.showSellingPlans = flag;
  }

  /**
   * Removes the active state from the currently active selling plan item.
   */
  removeActiveElement() {
    const activeElement = this.section.querySelector(
      `.selling-plan-item[data-selling-plan-active="true"]`
    );
    activeElement.dataset.sellingPlanActive = false;
  }

  /**
   * Handles variant price updates dispatched by the variant dropdown selector.
   * @param {CustomEvent} event
   */
  handleVariantPriceUpdate(event) {
    const priceInCents = Number(event?.detail?.price);
    if (Number.isNaN(priceInCents)) return;

    const compareAtPriceInCents =
      typeof event?.detail?.compareAtPrice === "number"
        ? event.detail.compareAtPrice
        : null;

    this.updateOneTimePrice(priceInCents, compareAtPriceInCents);
    this.updateSubscriptionPrice(priceInCents, compareAtPriceInCents);
  }

  /**
   * Updates the one-time purchase price display.
   * @param {number} priceInCents
   */
  updateOneTimePrice(priceInCents, compareAtPriceInCents) {
    if (this.oneTimePriceElement) {
      this.oneTimePriceElement.innerHTML = this.formatPrice(priceInCents);
    }
    this.updateComparePriceElement(
      this.oneTimeComparePriceElement,
      compareAtPriceInCents,
      priceInCents
    );
  }

  /**
   * Updates the subscription price display.
   * @param {number} priceInCents
   */
  updateSubscriptionPrice(priceInCents, compareAtPriceInCents) {
    if (!this.subscriptionPriceElement) return;
    const adjustedPrice = this.calculateSubscriptionPrice(priceInCents);
    this.subscriptionPriceElement.innerHTML = this.formatPrice(adjustedPrice);
    this.updateComparePriceElement(
      this.subscriptionComparePriceElement,
      compareAtPriceInCents,
      adjustedPrice
    );
  }

  /**
   * Updates the compare price element visibility/content.
   * @param {HTMLElement|null} element
   * @param {number|null} comparePriceInCents
   * @param {number} currentPriceInCents
   */
  updateComparePriceElement(
    element,
    comparePriceInCents,
    currentPriceInCents
  ) {
    if (!element) return;
    if (this.shouldShowComparePrice(comparePriceInCents, currentPriceInCents)) {
      element.innerHTML = this.formatPrice(comparePriceInCents);
      element.hidden = false;
    } else {
      element.hidden = true;
      element.innerHTML = "";
    }
  }

  /**
   * Determines if compare price should be displayed.
   * @param {number|null} comparePriceInCents
   * @param {number} currentPriceInCents
   * @returns {boolean}
   */
  shouldShowComparePrice(comparePriceInCents, currentPriceInCents) {
    if (
      typeof comparePriceInCents !== "number" ||
      Number.isNaN(comparePriceInCents)
    )
      return false;

    return comparePriceInCents > currentPriceInCents;
  }

  /**
   * Calculates the selling-plan price based on the adjustment configuration.
   * @param {number} priceInCents
   * @returns {number}
   */
  calculateSubscriptionPrice(priceInCents) {
    if (this.priceAdjustmentType === "percentage" && this.priceAdjustmentValue) {
      const discount = Math.round(
        (priceInCents * this.priceAdjustmentValue) / 100
      );
      return priceInCents - discount;
    }
    return priceInCents;
  }

  /**
   * Formats a price in cents to match the storefront currency string.
   * @param {number} priceInCents
   * @returns {string}
   */
  formatPrice(priceInCents) {
    if (
      typeof Shopify !== "undefined" &&
      typeof Shopify.formatMoney === "function"
    ) {
      const formatted = Shopify.formatMoney(
        priceInCents,
        window?.theme?.moneyFormat || "${{amount}}"
      );
      return formatted.replace(".00", "");
    }

    const dollars = (priceInCents / 100).toFixed(2);
    if (dollars.endsWith(".00")) {
      return `$${dollars.slice(0, -3)}`;
    }
    return `$${dollars}`;
  }
}

customElements.define(
  "pdp-product-overview-selling-plan",
  PDPProductOverviewSellingPlan
);
